import { useEffect, useState } from "react";
import type { BoardListResponse, BoardResponse } from "../api/contract.gen.ts";
import { useServices } from "../app/services.ts";
import { label, shortTime } from "../data/display.ts";
import { useStore } from "../data/store.ts";
import { Icon } from "../ui/Icon.tsx";
import styles from "../chats/TopicPanel.module.css";
import own from "./BoardList.module.css";

const TEMPLATES: [string, string][] = [
  ["blank", "Leer"],
  ["flow", "Ablauf"],
  ["mindmap", "Mindmap"],
  ["floor_plan", "Grundriss"],
  ["week_plan", "Wochenplan"],
];

/** The canvases of a chat, next to it, with "Neues Canvas" (app: BoardList, template sheet). */
export function BoardList({ conversationId, onOpen, onClose }: { conversationId: string; onOpen: (boardId: string) => void; onClose: () => void }) {
  const services = useServices();
  const conversation = useStore((s) => s.conversations.get(conversationId));
  const [boards, setBoards] = useState<BoardResponse[]>();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [template, setTemplate] = useState("blank");
  const [style, setStyle] = useState<"sketch" | "clean">("sketch");
  const [error, setError] = useState<string>();

  useEffect(() => {
    services.session.call<BoardListResponse>(`/v1/conversations/${conversationId}/boards`).then((r) => setBoards(r.boards), () => setBoards([]));
  }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function create() {
    const name = title.trim() || "Canvas";
    try {
      const board = await services.session.call<BoardResponse>(`/v1/conversations/${conversationId}/boards`, { method: "POST", json: { title: name, template: template === "blank" ? undefined : template, style, client_id: crypto.randomUUID() } });
      // The card in the chat, as the app sends it.
      await services.sendText(conversationId, `🖼 Neues Canvas: ${name}`, { board_id: board.id, board_card: true }).catch(() => undefined);
      onOpen(board.id);
    } catch {
      setError("Das Canvas ließ sich nicht anlegen.");
    }
  }

  const names = (id: string | null | undefined) => label(conversation?.members.find((m) => m.user.id === id)?.user).split(" ")[0];
  return (
    <aside className={styles.panel} aria-label="Canvas">
      <header className={styles.head}>
        <Icon name="board" />
        <b>Canvas</b>
        <button onClick={onClose} aria-label="Schließen">
          <Icon name="close" />
        </button>
      </header>
      <div className={styles.body}>
        {!creating ? (
          <button className={styles.playAll} onClick={() => setCreating(true)}>
            <Icon name="add" size={18} /> Neues Canvas
          </button>
        ) : (
          <div className={own.form}>
            <input autoFocus placeholder="Name" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && void create()} maxLength={80} />
            <div className={own.choices}>
              {TEMPLATES.map(([id, name]) => (
                <button key={id} className={template === id ? own.on : undefined} onClick={() => setTemplate(id)}>
                  {name}
                </button>
              ))}
            </div>
            <div className={own.choices}>
              <button className={style === "sketch" ? own.on : undefined} onClick={() => setStyle("sketch")}>
                Skizze
              </button>
              <button className={style === "clean" ? own.on : undefined} onClick={() => setStyle("clean")}>
                Sauber
              </button>
            </div>
            <button className={styles.playAll} onClick={() => void create()}>
              Anlegen
            </button>
            {error && <p className={styles.note}>{error}</p>}
          </div>
        )}
        <div className={styles.label}>IN DIESEM CHAT</div>
        {!boards && <p className={styles.note}>Wird geladen …</p>}
        {boards?.length === 0 && <p className={styles.note}>Noch kein Canvas.</p>}
        {boards?.map((b) => (
          <div key={b.id} className={styles.segment}>
            <button className={styles.text} onClick={() => onOpen(b.id)}>
              <b>{b.title}</b>
              <span>
                {b.present?.length ? `${b.present.map(names).join(", ")} zeichnet · ` : ""}
                {b.element_count} Elemente · {shortTime(b.updated_at)} von {b.updated_by === services.store.me ? "dir" : names(b.updated_by)}
              </span>
            </button>
            <button className={styles.play} onClick={() => onOpen(b.id)} aria-label={`${b.title} öffnen`}>
              <Icon name="open" size={18} />
            </button>
          </div>
        ))}
      </div>
    </aside>
  );
}
